import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL as API_BASE } from '../config/api';

const PrivacyPolicyPage = () => {
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'Privacy Policy | Aureate Residences Ltd';
    window.scrollTo(0, 0);
    Promise.all([
      axios.get(`${API_BASE}/privacy-policy`).then(r => r.data).catch(() => null),
    ]).then(([p]) => {
      setPolicy(p);
      setLoading(false);
    });
  }, []);

  const fallbackSections = [
    {
      title: 'Information we collect',
      content: 'We collect information you provide directly to us when you fill out a contact form, request information about our services or communicate with us. This may include your name, email address, phone number, organisation name, location and any other information you choose to provide.'
    },
    {
      title: 'How we use your information',
      content: 'We use the information we collect to respond to your enquiry, to provide and improve our accommodation and property services, to communicate with you about arrangements you have asked about, and, with your consent, to send updates about Aureate Residences.'
    },
    {
      title: 'Legal basis for processing',
      content: 'We process your personal data on the basis of legitimate interest (responding to a genuine enquiry), contract performance (where we enter into an accommodation or property arrangement with you), and consent (for marketing communications). You may withdraw consent at any time by contacting us.'
    },
    {
      title: 'Information sharing and third parties',
      content: 'We do not sell your personal information. We may share information with the following categories of third party, solely as necessary to provide our services:\n\n• Hosting and infrastructure providers (cloud servers, databases)\n• Email delivery services (for sending confirmations and notifications)\n• Analytics services (to understand how our website is used)\n• Payment processors (where applicable)\n• Property compliance and maintenance contractors (where relevant to an active arrangement)\n\nAll third parties are bound by data-processing agreements and process data only on our instructions.'
    },
    {
      title: 'Data security',
      content: 'We implement appropriate technical and organisational measures to protect your personal information against unauthorised access, alteration, disclosure or destruction. These include encrypted connections (TLS), access controls and regular security reviews.'
    },
    {
      title: 'Data retention',
      content: 'We retain your personal data only as long as necessary for the purpose it was collected, or as required by law. Enquiry data is retained for up to 24 months after your last contact with us. Data relating to an active arrangement is retained for the duration of the arrangement plus six years.'
    },
    {
      title: 'Your rights',
      content: 'Under UK data-protection law you have the right to access, correct or delete your personal information, to restrict or object to its processing, and to data portability. You also have the right to lodge a complaint with the Information Commissioner\'s Office (ICO). To exercise any of these rights, please contact us at info@aureate-resident.com.'
    },
    {
      title: 'Cookies',
      content: 'Our website may use cookies and similar technologies to improve your browsing experience and to collect analytics data. You can control cookie preferences through your browser settings.'
    },
    {
      title: 'Contact us',
      content: 'If you have any questions about this Privacy Policy, please contact us at info@aureate-resident.com or call +44 131 381 1989.'
    }
  ];

  const sections = (policy?.sections?.length > 0) ? policy.sections : fallbackSections;
  const lastUpdated = policy?.last_updated || new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

  if (loading) {
    return (
      <div style={{ background: 'var(--black)', minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: 32, height: 32, border: '2px solid var(--gold)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin-z 1s linear infinite' }} />
      </div>
    );
  }

  return (
    <div style={{ background: 'var(--black)' }}>
      {/* Hero */}
      <section className="approach" style={{ borderTop: 0, paddingBottom: 'clamp(40px, 5vw, 64px)' }}>
        <div className="wrap" style={{ maxWidth: 820 }}>
          <div className="head" style={{ marginBottom: 0 }}>
            <span className="eyebrow">Legal</span>
            <h2>Privacy policy.</h2>
            <p style={{ fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: '.1em', color: 'var(--mute)' }}>
              Last updated: {lastUpdated}
            </p>
            <p>
              {policy?.introduction || 'Aureate Residences Ltd ("we", "our" or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose and safeguard your information when you visit our website or use our services.'}
            </p>
          </div>
        </div>
      </section>

      {/* Sections */}
      <section style={{ paddingBlock: '0 clamp(80px, 11vw, 140px)' }}>
        <div className="wrap" style={{ maxWidth: 820 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 48 }}>
            {sections.map((section, i) => (
              <div key={i} style={{ borderLeft: '2px solid rgba(212,175,55,.3)', paddingLeft: 24 }}>
                <h3 style={{ fontFamily: 'var(--serif)', fontWeight: 500, fontSize: 'clamp(22px, 2.2vw, 28px)', lineHeight: 1.2, marginBottom: 12, color: 'var(--ink)' }}>
                  {i + 1}. {section.title}
                </h3>
                <p style={{ color: 'var(--soft)', fontSize: 16, lineHeight: 1.65, whiteSpace: 'pre-line' }}>
                  {section.content}
                </p>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 64, paddingTop: 32, borderTop: '1px solid var(--line-soft)' }}>
            <p style={{ color: 'var(--mute)', fontSize: 14 }}>
              This privacy policy is subject to change. Please review it periodically for updates.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicyPage;
